import { ReactNode, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useErrorNotification } from '@providers/ErrorsNotificationProvider';
import { useBlocker } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { AnimatedNode } from '@ui/components/Animation';
import { AnimationCrossfade } from '@ui/components/AnimationCrossfade';
import { BookmarkButton } from '@ui/components/BookmarkButton';
import { BookmarkButtonBar } from '@ui/components/BookmarkButtonBar';
import { Loader } from '@ui/components/Loader';
import { PageTabs } from '@ui/components/PageTabs';
import { CopyIcon } from '@ui/icons/CopyIcon';
import { DeleteIcon } from '@ui/icons/DeleteIcon';
import { SaveIcon } from '@ui/icons/SaveIcon';

import { Tournament, UpdateTournamentRequest } from '@features/shared/api/typings';
import { SimpleSubHeader } from '@features/shared/ui/SimpleSubHeader';
import { UnsavedChangesModal } from '@features/shared/ui/UnsavedChangesModal/UnsavedChangesModal';
import { TournamentSettings } from '@features/tournament/ui/components/TournamentSettings';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings';
import {
  getDefaultValuesFromTournament,
  getSubmitValuesFromForm,
} from '@features/tournament/ui/components/TournamentSettings';

import styles from './TournamentEditPage.module.css';

const TABS = [
  { value: 'settings', label: 'Настройки' } as const,
  { value: 'participants', label: 'Участники' } as const,
];

type PageTab = (typeof TABS)['0']['value'];

export type Props = {
  tournament: Tournament;
  pageTab: PageTab;
  onPageTabChange: (page: PageTab) => void;
  onBack: () => void;
  onDelete?: () => void;
  onSave?: (tournament: UpdateTournamentRequest, opts?: { noRedirect?: boolean }) => Promise<void>;
  onCopy?: () => void;
  participants?: ReactNode;
  onUploadLogo: (file: FormData) => Promise<void>;
  onUploadRules: (file: FormData) => Promise<void>;
  onUploadSponsorLogo: (sponsorNumber: number, file: FormData) => Promise<void>;
  disabled: boolean;
  canForceDelete?: boolean;
  isDeleting?: boolean;
  enableUnsavedWarning?: boolean;
  isAdmin: boolean;
  isVideoAvailable?: boolean;
  _useNavigationBlocker?: typeof useBlocker; // HACK: для сторей
};

export function TournamentEditPage({
  tournament,
  pageTab,
  onPageTabChange,
  participants,
  onBack,
  onDelete,
  onSave,
  onCopy,
  onUploadLogo,
  onUploadRules,
  onUploadSponsorLogo,
  disabled,
  canForceDelete,
  isDeleting,
  enableUnsavedWarning,
  isAdmin,
  isVideoAvailable,
  // для storybook
  _useNavigationBlocker = useBlocker,
}: Props) {
  const isMatch = tournament.system === 'OneOnOne';
  const methods = useForm<TournamentEditFormValues>({
    disabled,
    defaultValues: getDefaultValuesFromTournament(tournament),
  });
  const { error, reset } = useErrorNotification();
  let errStr = error?.name === 'UserError' && error?.message;
  try {
    const parsed = errStr && JSON.parse(errStr);
    errStr = parsed && parsed.message;
    if (!errStr && parsed && parsed.errors) {
      errStr = Object.keys(parsed.errors)
        .map((key) => `${key}: ${parsed.errors[key].join('; ')}`)
        .join('\n');
    }
  } catch (_e: unknown) {
    console.error('Failed to parse error in tournament edit', error);
  }

  const onError = () => {
    //console.log(error);
  };

  const { isSubmitting, dirtyFields } = methods.formState;

  // Предупреждение о несохранённых данных
  // ВАЖНО: formState.isDirty ненадёжен и даёт false positives
  const isDirty = Object.keys(dirtyFields).length > 0;
  const unsavedWarningCondition = enableUnsavedWarning && isDirty && !disabled;
  const unsavedBlock = _useNavigationBlocker({
    condition: unsavedWarningCondition,
  });
  const [ignoredWarning, setIgnoredWarning] = useState(false);

  //

  const handleSubmit = methods.handleSubmit(async (tournament: TournamentEditFormValues) => {
    reset();
    const r = await onSave?.(getSubmitValuesFromForm(tournament));

    methods.reset(tournament);

    return r;
  }, onError);
  const handleSave = methods.handleSubmit(async (tournament: TournamentEditFormValues) => {
    reset();
    const r = await onSave?.(getSubmitValuesFromForm(tournament), {
      noRedirect: true,
    });

    methods.reset(tournament);

    return r;
  }, onError);

  const title = methods.watch('title') ?? tournament.title ?? 'Без названия';
  const draftBadge = tournament.status === 'Draft' ? '[Черновик] ' : undefined;
  const titleRow = (
    <span
      className={cx(styles.title, {
        [styles.draft]: tournament.status === 'Draft',
      })}
    >
      {draftBadge}
      {title}
    </span>
  );

  useEffect(() => {
    methods.setValue('logoUrl', tournament.logoUrl);
    methods.setValue('rulesUrl', tournament.rulesUrl);
    methods.setValue('sponsors', tournament.sponsors || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament.logoUrl, tournament.rulesUrl, tournament.sponsors]);

  return (
    <>
      <SimpleSubHeader title={titleRow} onBack={onBack} />

      {!tournament && <Loader className={styles.loader} centered />}

      {tournament && (
        <FormProvider {...methods}>
          <div className={styles.root}>
            <PageTabs
              className={styles.tabs}
              tabs={TABS}
              onChange={(t) => {
                setTimeout(() => setIgnoredWarning(false), 100);
                onPageTabChange(t);
              }}
              value={pageTab}
            />
            <div className={styles.error}>{errStr}</div>
            <AnimationCrossfade contentKey={pageTab}>
              {pageTab !== 'participants' ? (
                <form onSubmit={handleSubmit}>
                  <TournamentSettings
                    clubId={tournament.clubId}
                    className={cx({
                      [styles.submitting]: isSubmitting,
                    })}
                    onUploadRules={onUploadRules}
                    onUploadLogo={onUploadLogo}
                    onUploadSponsorLogo={onUploadSponsorLogo}
                    isAdmin={isAdmin}
                    isVideoAvailable={isVideoAvailable}
                  />
                </form>
              ) : (
                participants
              )}
            </AnimationCrossfade>
          </div>

          <BookmarkButtonBar>
            {!disabled && (
              <BookmarkButton
                icon={<SaveIcon />}
                label="Сохранить"
                onClick={handleSubmit}
                disabled={disabled || isSubmitting}
              />
            )}
            {onCopy && (
              <BookmarkButton
                icon={<CopyIcon />}
                label="Скопировать"
                clickedStateTop={<div>Скопировать турнир?</div>}
                clickedStateBottom={
                  <div className={styles.clickable} onClick={onCopy}>
                    Да, скопировать
                  </div>
                }
              />
            )}
            {(!disabled || canForceDelete) && (
              <BookmarkButton
                icon={<DeleteIcon />}
                label="Удалить"
                danger={canForceDelete}
                clickedStateBottom={
                  <div className={styles.clickable} onClick={onDelete}>
                    Да, удалить
                  </div>
                }
                clickedStateTop={
                  <div>
                    {canForceDelete
                      ? 'Удалить принудительно?'
                      : `Удалить ${isMatch ? 'матч' : 'турнир'}`}
                  </div>
                }
                disabled={isSubmitting || isDeleting}
              />
            )}
          </BookmarkButtonBar>

          {/* Предупреждение о несохранённых изменениях */}
          <AnimatedNode>
            {unsavedBlock.status === 'blocked' && (
              <UnsavedChangesModal onProceed={unsavedBlock.proceed} onStay={unsavedBlock.reset} />
            )}
          </AnimatedNode>

          {/* Предупреждение о переходе на другой таб */}
          <AnimatedNode>
            {unsavedWarningCondition && !ignoredWarning && pageTab === 'participants' && (
              <UnsavedChangesModal
                showSave
                hideProceed
                pending={isSubmitting}
                labelSay="Игнорировать"
                onSave={handleSave}
                onStay={() => setIgnoredWarning(true)}
              >
                <h1>Сохранить настройки?</h1>
                <p>Рекомендуется сохранить изменения настроек перед редактированием участников</p>
              </UnsavedChangesModal>
            )}
          </AnimatedNode>
        </FormProvider>
      )}
    </>
  );
}

export function TournamentEditPageSkeleton() {
  return (
    <>
      <SimpleSubHeader title="" />
      <Loader className={styles.loader} centered />
    </>
  );
}
